import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';

export interface Job {
    id: string;
    type: string;
    status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
    message?: string;
    progress?: number;
    onCancel?: (id: string) => Promise<void> | void;
    startTime: number;
}

interface JobContextType {
    jobs: Job[];
    addJob: (type: string, id: string, options?: Pick<Job, 'message' | 'progress' | 'onCancel'>) => void;
    updateJobStatus: (id: string, status: Job['status'], message?: string, progress?: number) => void;
    removeJob: (id: string) => void;
    isJobRunning: (id: string) => boolean;
    activeJobsCount: number;
}

const JobContext = createContext<JobContextType | undefined>(undefined);

export const JobProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [jobs, setJobs] = useState<Job[]>([]);

    const addJob = useCallback((type: string, id: string, options?: Pick<Job, 'message' | 'progress' | 'onCancel'>) => {
        setJobs((prev) => [
            ...prev.filter((job) => job.id !== id),
            {
                id,
                type,
                status: 'running',
                startTime: Date.now(),
                message: options?.message,
                progress: options?.progress,
                onCancel: options?.onCancel,
            },
        ]);
    }, []);

    const updateJobStatus = useCallback((id: string, status: Job['status'], message?: string, progress?: number) => {
        setJobs((prev) =>
            prev.map((job) =>
                job.id === id ? { ...job, status, message, progress } : job
            )
        );
    }, []);

    const removeJob = useCallback((id: string) => {
        setJobs((prev) => prev.filter((job) => job.id !== id));
    }, []);

    const isJobRunning = useCallback((id: string) => {
        return jobs.some((job) => job.id === id && job.status === 'running');
    }, [jobs]);

    const activeJobsCount = jobs.filter(j => j.status === 'running' || j.status === 'pending').length;

    return (
        <JobContext.Provider value={{ jobs, addJob, updateJobStatus, removeJob, isJobRunning, activeJobsCount }}>
            {children}
        </JobContext.Provider>
    );
};

export const useJobs = () => {
    const context = useContext(JobContext);
    if (context === undefined) {
        throw new Error('useJobs must be used within a JobProvider');
    }
    return context;
};
